import {forwardRef} from 'react';

import {css, tokens, useTokenValue, view} from '@sail/ui';

import {
  ChatStartedRow,
  CustomComponent,
  GroupedMessagesRow,
  MetaEventRow,
  ParticipantJoinedRow,
  ParticipantLeftRow,
  TranslationRow,
} from '@stripe-internal/conversations-ui';
import {
  Conversation,
  ConversationLogItem,
  ConversationMessage,
  TextConversationMessage,
} from '@stripe-internal/conversations-client';

type Props = {
  conversation: Conversation;
  items: ConversationLogItem[];
  animated?: boolean;
  disableInteraction?: boolean;
};

const groupedMessageTypes = new Set([
  'text-conversation-message',
  'rich-text-conversation-message',
  'attachment-conversation-message',
  'inline-data-conversation-message',
  'articles-conversation-message',
]);

function isTextConversationMessage(
  message: ConversationMessage,
): message is TextConversationMessage {
  return (
    (message as TextConversationMessage).type === 'text-conversation-message'
  );
}

function GroupedItems({
  conversation,
  items,
  animated,
  disableInteraction,
}: {
  conversation: Conversation;
  items: ConversationLogItem[];
  animated: boolean;
  disableInteraction: boolean;
}) {
  const rows = [];

  let translationRowAdded = false; // Flag to track if the TranslationRow has been added
  let conversationParticipantJoined = false; // Flag to track if the agent has joined the chat

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];

    switch (item.name) {
      case 'conversation-participant-joined': {
        conversationParticipantJoined = true;
        rows.push(
          <ParticipantJoinedRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-participant-left': {
        rows.push(
          <ParticipantLeftRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-message-added': {
        const participantMessages = [item];
        const {participant} = item.message;

        const isTranslationCase =
          isTextConversationMessage(item.message) &&
          !!item.message.originalText;

        if (
          isTranslationCase &&
          !translationRowAdded &&
          conversationParticipantJoined // Only show this when an agent has joined. Without this condition UH can trigger the row render.
        ) {
          rows.push(<TranslationRow />);
          translationRowAdded = true; // Update the flag to reflect that the TranslationRow has been added
        }

        // walk forward grouping all messages for this participant
        for (let j = i + 1; j < items.length; j += 1) {
          const nextItem = items[j];

          if (nextItem.name === 'conversation-message-added') {
            const isGroupedType =
              nextItem.message.participant.id === participant.id &&
              groupedMessageTypes.has(nextItem.message.type);

            if (isGroupedType) {
              participantMessages.push(nextItem);
              i = j;

              // eslint-disable-next-line no-continue
              continue;
            }
          }

          break;
        }

        rows.push(
          <GroupedMessagesRow
            conversation={conversation}
            key={`grouped-${item.id}`}
            items={participantMessages}
            disableInteraction={disableInteraction}
            animated={animated}
          />,
        );
        break;
      }
      case 'conversation-meta': {
        rows.push(<MetaEventRow key={item.id} item={item} />);
        break;
      }
      case 'conversation-started': {
        rows.push(<ChatStartedRow key={item.id} conversation={conversation} />);
        break;
      }
      case 'conversation-custom-component': {
        rows.push(<CustomComponent {...item} />);
        break;
      }
      case 'conversation-ended': {
        break;
      }
      default:
      // unknown log item
    }
  }

  return <>{rows}</>;
}

export const MobileLogItems = forwardRef<HTMLDivElement, Props>(
  ({conversation, items, animated, disableInteraction}, ref) => {
    const largeSpace = parseInt(useTokenValue(tokens.space.large), 10);
    const xsmallSpace = parseInt(useTokenValue(tokens.space.xsmall), 10);
    const tightGap = -largeSpace + xsmallSpace;

    return (
      <view.div
        ref={ref}
        data-testid="conversation-log-items"
        className="uh-conversations-new-design"
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'large',
        }}
        uses={[
          css.global({
            '.uh-conversations-new-design .uh-conversations-contains-meta-row + .uh-conversations-contains-meta-row':
              {marginTop: '-16px'},

            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="user"] + .uh-conversations-text-message-row[data-message-type="user"]':
              {marginTop: tightGap},
            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="agent"] + .uh-conversations-text-message-row[data-message-type="agent"]':
              {marginTop: tightGap},
            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="agent"] + [data-testid="typing-indicator"]':
              {marginTop: tightGap},

            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="user"]:last-child .uh-conversations-chat-bubble':
              {borderBottomRightRadius: '4px !important'},
            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="agent"]:last-child .uh-conversations-chat-bubble':
              {borderBottomLeftRadius: '4px !important'},

            '.uh-conversations-new-design .uh-conversations-message-added:empty':
              {display: 'none'},
            '.uh-conversations-new-design .uh-conversations-grouped-messages.v2.fill-space:last-of-type':
              {
                minHeight: 'calc(var(--log-height) - 70px)',
              },
          }),
        ]}
      >
        <GroupedItems
          conversation={conversation}
          items={items}
          animated={!!animated}
          disableInteraction={!!disableInteraction}
        />
      </view.div>
    );
  },
);
